import React, { useState, useEffect } from 'react';
import { UserCheck, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const AttributionModal = ({ client, onClose, onSuccess }) => {
  const [vendorCode, setVendorCode] = useState(client.vendor?.vendor_code || '');
  const [referrerCode, setReferrerCode] = useState(client.referrer?.referral_code || '');
  const [availableVendors, setAvailableVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(true);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/admin/available-vendors');
      setAvailableVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {};
      
      // Vendor code (empty string pour "direct")
      if (vendorCode !== (client.vendor?.vendor_code || '')) {
        payload.vendor_code = vendorCode || null;
      }
      
      // Referrer code
      if (referrerCode !== (client.referrer?.referral_code || '')) {
        payload.referrer_code = referrerCode || null;
      }
      
      if (Object.keys(payload).length === 0) {
        alert('Aucune modification détectée');
        setLoading(false);
        return;
      }
      
      await api.put(`/admin/users/${client.id}/attribution`, payload);
      
      alert('Attribution modifiée avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#1A1A1D] rounded-lg metallic-border max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center space-x-2">
            <UserCheck className="h-5 w-5 text-[#00FFFF]" />
            <span>Modifier Attribution</span>
          </h3>
          <button onClick={onClose} className="text-[#6B6B6E] hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-4 bg-[#222226] rounded-lg">
          <p className="text-[#B8B8BA] text-sm">Client</p>
          <p className="text-white font-semibold">{client.full_name}</p>
          <p className="text-[#6B6B6E] text-sm">{client.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Vendeur */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Vendeur</label>
            {loadingVendors ? (
              <div className="text-[#B8B8BA] text-sm">Chargement...</div>
            ) : (
              <select
                value={vendorCode}
                onChange={(e) => setVendorCode(e.target.value)}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              >
                <option value="">-- Client Direct (pas de vendeur) --</option>
                {availableVendors.map((vendor) => (
                  <option key={vendor.vendor_code} value={vendor.vendor_code}>
                    {vendor.full_name} - {vendor.email} ({vendor.vendor_code})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Parrain */}
          <div>
            <label className="block text-[#E8E8E8] text-sm mb-2">Code Parrain</label>
            <input
              type="text"
              value={referrerCode}
              onChange={(e) => setReferrerCode(e.target.value.toUpperCase())}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
              placeholder="CODE123 ou laisser vide"
            />
            <p className="text-[#6B6B6E] text-xs mt-1">
              Laisser vide pour retirer le parrain
            </p>
          </div>

          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg">
            <p className="text-[#FFB800] text-sm">
              ⚠️ Cette modification n'affecte pas les commissions passées. Seules les ventes/renouvellements futurs seront attribués au nouveau vendeur/parrain.
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary py-3 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Traitement...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span>Sauvegarder</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AttributionModal;
